import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    styled,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";

const StyledButton = styled(Button)(({ theme }) => ({
    background: "#4D6BF6",
    color: "#ffffff",
    boxShadow: "none",
    border: "1px solid #4D6BF6",
    whiteSpace: "nowrap",
    fontSize: "16px",
    fontWeight: "500",
    borderRadius: "10px",
    padding: ".5vh 1vw",
    letterSpacing: "0",
    "&:hover": {
        borderColor: "#4D6BF6",
        color: "#ffffff",
        backgroundColor: "#4D6BF6",
    },
    "&:disabled": {
        opacity: 0.7,
        borderColor: "#4D6BF6",
        color: "#ffffff",
        backgroundColor: "#4D6BF6",
        cursor: "not-allowed",
    },
}));

const ErrorButton = styled(Button)(({ theme }) => ({
    background: "#DC2626",
    color: "#ffffff",
    boxShadow: "none",
    border: "1px solid #DC2626",
    whiteSpace: "nowrap",
    fontSize: "16px",
    fontWeight: "500",
    borderRadius: "10px",
    padding: ".5vh 1vw",
    letterSpacing: "0",
    "&:hover": {
        borderColor: "#DC2626",
        color: "#ffffff",
        backgroundColor: "#B91C1C",
    },
    "&:disabled": {
        opacity: 0.7,
        borderColor: "#DC2626",
        color: "#ffffff",
        backgroundColor: "#DC2626",
        cursor: "not-allowed",
    },
}));

const GreyStyledButton = styled(Button)(({ theme }) => ({
    background: "#fff",
    color: "#0D0D0E",
    boxShadow: "none",
    border: "1px solid #D7D8D9",
    whiteSpace: "nowrap",
    fontSize: "16px",
    fontWeight: "500",
    borderRadius: "10px",
    padding: ".5vh 1vw",
    letterSpacing: "0",
}));

const DeleteBoqModal = ({
    open,
    onClose,
    boqData,
    onDeleteBoq,
}) => {
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!open) {
            setIsDeleting(false);
        }
    }, [open]);

    const handleDelete = async () => {
        if (isDeleting || !boqData?.id) return;

        setIsDeleting(true);
        try {
            await onDeleteBoq(boqData.id, boqData.inclusionId);
            onClose();
        } catch (error) {
            console.error("Error deleting BOQ:", error);
            setIsDeleting(false);
        }
    };

    if (!boqData) return null;

    const boqImage =
        boqData.image && boqData.image.length > 0 ? boqData.image[0]?.image : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    padding: "8px",
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "#0D0D0E",
                    paddingBottom: "16px",
                }}
            >
                Delete BOQ Item
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                        Are you sure you want to delete this BOQ item?
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            p: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                        }}
                    >
                        <Box
                            sx={{
                                width: "45px",
                                height: "45px",
                                borderRadius: "8px",
                                border: "2px solid white",
                                boxShadow: "0 4px 2.67px rgba(0,0,0,0.15)",
                                backgroundColor: "#e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                flexShrink: 0,
                            }}
                        >
                            {boqImage ? (
                                <img
                                    src={boqImage}
                                    alt={boqData.item_name}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                    }}
                                />
                            ) : (
                                <ImageIcon sx={{ fontSize: 24, color: "#949DAA" }} />
                            )}
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 500, color: "#0D0D0E", fontSize: "14px" }}
                            >
                                {boqData.item_name || "-"}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: "#949DAA", fontSize: "12px" }}
                            >
                                {boqData.type || "-"} • {boqData.subcategory || "-"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{ padding: "16px 24px", justifyContent: "space-between" }}
            >
                <GreyStyledButton onClick={onClose} disabled={isDeleting}>
                    Cancel
                </GreyStyledButton>

                <ErrorButton onClick={handleDelete} disabled={isDeleting}>
                    {isDeleting ? (
                        <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Deleting...
                        </>
                    ) : (
                        "Delete"
                    )}
                </ErrorButton>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteBoqModal;

